import { useRef, useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { videos } from '../../assets';
import './ScrollCinematicExperience.css';

/**
 * ScrollCinematicExperience
 * 
 * A scroll-driven cinematic video experience. The video scrubs as the user
 * scrolls, and text beats appear synchronized to the video timeline.
 * 
 * The INITIAL STATE (before scrolling) shows the hero title, subtitle, and
 * CTA buttons — like pear.no shows its headline on load. The first frames
 * of the video have a subtle breathing movement. Once the user scrolls,
 * these elements fade out and the cinematic timeline takes over.
 */

// ─── Beat Definitions ──────────────────────────────────────────
// "initial" is the pre-scroll state. The rest are scroll-driven beats.
const BEATS = {
  // Shown BEFORE scrolling — fades out as scroll starts
  initial: { fadeOutStart: 0.03, fadeOutEnd: 0.12 },
  // Scroll-driven beats: ágiles y equilibrados
  about: {
    title: { enter: 0.15, peak: 0.22, exit: 0.44 },
    body: { enter: 0.18, peak: 0.25, exit: 0.44 },
    badges: { enter: 0.22, peak: 0.29, exit: 0.44 },
  },
  // ctaFinal: visible de 0.48 a 0.68 para dar paso limpio a la cortina de Tarifas
  ctaFinal: { enter: 0.48, peak: 0.56, exit: 0.68 },
};

// ─── Utility: Calculate beat opacity ────────────────────────────
function beatOpacity(progress, beat) {
  if (progress < beat.enter) return 0;
  if (progress > beat.exit) return 0;
  if (progress < beat.peak) {
    return (progress - beat.enter) / (beat.peak - beat.enter);
  }
  if (progress <= beat.exit - 0.04) return 1;
  return Math.max(0, (beat.exit - progress) / 0.04);
}

// ─── Utility: Calculate beat reveal progress (0–1) ──────────────
function beatReveal(progress, beat) {
  if (progress < beat.enter) return 0;
  if (progress >= beat.peak) return 1;
  return (progress - beat.enter) / (beat.peak - beat.enter);
}

const ScrollCinematicExperience = () => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const rafRef = useRef(null);
  const currentTimeRef = useRef(0);
  const idleVideoRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const prefersReducedMotion = useRef(false);

  // Check for reduced motion preference
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.current = mq.matches;
    const handler = (e) => { prefersReducedMotion.current = e.matches; };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Video ready handler
  const handleVideoReady = useCallback(() => {
    setVideoReady(true);
    setInitialLoaded(true);
  }, []);

  // Ensure initial entrance animation triggers reliably across all mobile browsers + unlock iOS video pipeline
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoaded(true);
    }, 200);

    const video = videoRef.current;
    if (video) {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      // Despertar el decodificador de video de iOS Safari
      const wakeVideo = () => {
        setVideoReady(true);
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            video.pause();
          }).catch(() => {
            // Ignorar restricciones de autoplay si ya está listo
          });
        }
      };

      if (video.readyState >= 1 || video.duration) {
        wakeVideo();
      }

      const onMetadata = () => wakeVideo();
      video.addEventListener('loadedmetadata', onMetadata);
      video.addEventListener('loadeddata', onMetadata);
      video.addEventListener('canplay', onMetadata);

      return () => {
        clearTimeout(timer);
        video.removeEventListener('loadedmetadata', onMetadata);
        video.removeEventListener('loadeddata', onMetadata);
        video.removeEventListener('canplay', onMetadata);
      };
    }
    return () => clearTimeout(timer);
  }, []);

  // ─── Idle Ping-Pong Playback Management ──────────────────────
  // Plays the smooth back-and-forth ping-pong video when idle at top,
  // pauses when scrolled away to conserve GPU/battery resources.
  useEffect(() => {
    const idleVideo = idleVideoRef.current;
    if (!idleVideo) return;

    if (progress <= 0.03) {
      idleVideo.playbackRate = 0.8;
      const playPromise = idleVideo.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Handled gracefully if browser restricts autoplay
        });
      }
    } else {
      idleVideo.pause();
    }
  }, [progress]);

  // ─── Main Scroll → Video Sync Loop ──────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    if (!container || !video) return;

    const isMobile = window.innerWidth < 768;
    // Factor de interpolación: 0.22 en desktop para seguimiento inmediato; 0.36 en móvil
    const lerpFactor = isMobile ? 0.36 : 0.22;
    let isActive = true;
    let isSeeking = false;
    let pendingTarget = null;
    let lastProgress = -1;

    const requestSeek = (time) => {
      if (!video.duration || isNaN(time)) return;
      const clamped = Math.max(0, Math.min(video.duration, time));

      if (video.seeking || isSeeking) {
        pendingTarget = clamped;
        return;
      }

      isSeeking = true;
      try {
        video.currentTime = clamped;
      } catch (err) {
        isSeeking = false;
      }
    };

    const handleSeeked = () => {
      isSeeking = false;
      if (pendingTarget !== null) {
        const next = pendingTarget;
        pendingTarget = null;
        if (Math.abs(video.currentTime - next) > 0.012) {
          requestSeek(next);
        }
      }
    };

    video.addEventListener('seeked', handleSeeked);

    const updateFrame = () => {
      if (!isActive) return;

      const rect = container.getBoundingClientRect();
      const containerHeight = container.offsetHeight;
      const viewportHeight = window.innerHeight;
      const scrollableDistance = containerHeight - viewportHeight;
      const scrolled = -rect.top;
      const rawProgress = Math.max(0, Math.min(1, scrolled / Math.max(1, scrollableDistance)));

      // Throttling de React state: solo actualiza si hay cambio visible (>0.002)
      // Esto ahorra decenas de re-renders por segundo en la CPU del celular y laptop
      if (Math.abs(rawProgress - lastProgress) > 0.002 || rawProgress === 0 || rawProgress === 1) {
        lastProgress = rawProgress;
        setProgress(rawProgress);
      }

      // Sincronización del video con el scroll
      if (video.duration && (videoReady || video.readyState >= 1)) {
        if (rawProgress > 0.02) {
          const maxAllowedTime = Math.max(0, video.duration - 0.06);
          // El scrub del video completa su recorrido a 0.68 para sostener la pose final durante la cortina
          const videoScrubProgress = Math.min(1, Math.max(0, (rawProgress - 0.02) / 0.66));
          const targetTime = Math.min(maxAllowedTime, videoScrubProgress * video.duration);

          if (prefersReducedMotion.current) {
            requestSeek(targetTime);
          } else {
            currentTimeRef.current += (targetTime - currentTimeRef.current) * lerpFactor;
            const nextTime = Math.max(0, Math.min(maxAllowedTime, currentTimeRef.current));
            requestSeek(nextTime);
          }
        } else {
          currentTimeRef.current = 0;
          if (video.currentTime !== 0) {
            requestSeek(0);
          }
        }
      }

      rafRef.current = requestAnimationFrame(updateFrame);
    };

    rafRef.current = requestAnimationFrame(updateFrame);

    return () => {
      isActive = false;
      video.removeEventListener('seeked', handleSeeked);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [videoReady]);

  // ─── Initial Hero State (visible before scroll) ──────────────
  // Fades out as user starts scrolling
  const initialFade = BEATS.initial;
  const initialOpacity = progress <= initialFade.fadeOutStart
    ? 1
    : progress >= initialFade.fadeOutEnd
      ? 0
      : 1 - (progress - initialFade.fadeOutStart) / (initialFade.fadeOutEnd - initialFade.fadeOutStart);

  // Parallax: initial content drifts up as user scrolls
  const initialTranslateY = progress * -120; // px upward

  // ─── Scroll-Driven Beat Calculations ────────────────────────
  const aboutTitleOpacity = beatOpacity(progress, BEATS.about.title);
  const aboutTitleReveal = beatReveal(progress, BEATS.about.title);
  const aboutBodyOpacity = beatOpacity(progress, BEATS.about.body);
  const aboutBodyReveal = beatReveal(progress, BEATS.about.body);
  const aboutBadgesOpacity = beatOpacity(progress, BEATS.about.badges);
  const aboutBadgesReveal = beatReveal(progress, BEATS.about.badges);
  const ctaOpacity = beatOpacity(progress, BEATS.ctaFinal);
  const ctaReveal = beatReveal(progress, BEATS.ctaFinal);

  // Salida hacia la siguiente sección (Curtain reveal de Tarifas):
  // Mantenemos la persona visible de fondo. Aumentamos suavemente el desenfoque (blur hasta 8px)
  // y aplicamos un oscurecimiento sutil (máximo ~25%) para que NUNCA desaparezca a negro puro.
  const exitScrimOpacity = progress > 0.68 ? Math.min(0.25, ((progress - 0.68) / 0.28) * 0.25) : 0;
  const videoBlur = progress > 0.65 ? Math.min(8, ((progress - 0.65) / 0.30) * 8) : 0;

  // En celular usamos el video ligero 720p con 61 keyframes para decodificación ultra rápida
  const isMobileDevice = typeof window !== 'undefined' && window.innerWidth < 768;
  const activeHeroVideo = isMobileDevice && videos.heroVideoMobile ? videos.heroVideoMobile : videos.heroVideo;

  return (
    <div ref={containerRef} className="cinematic-container">
      <div className="cinematic-sticky">
        {/* ─── MAIN SCROLL VIDEO ──────────────────── */}
        <video
          ref={videoRef}
          className="cinematic-video"
          src={activeHeroVideo}
          muted
          playsInline
          preload="auto"
          onLoadedData={handleVideoReady}
          style={{
            filter: videoBlur > 0 ? `blur(${videoBlur}px)` : 'none',
          }}
        />

        {/* ─── IDLE PING-PONG VIDEO (Smooth back-and-forth loop before scroll) ─── */}
        <video
          ref={idleVideoRef}
          className="cinematic-video"
          src={videos.heroIdleLoop}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onLoadedMetadata={(e) => {
            e.target.playbackRate = 0.8;
          }}
          style={{
            opacity: progress <= 0.03 ? 1 : 0,
            transition: 'opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
            pointerEvents: 'none',
          }}
        />

        {/* ─── SCRIM OVERLAY ──────────────────────── */}
        <div className="cinematic-scrim" />

        {/* ─── EXIT SCRIM ─────────────────────────── */}
        <div
          className="cinematic-exit-scrim"
          style={{ opacity: exitScrimOpacity }}
        />

        {/* ─── RETICULAR GRID OVERLAY ─────────────── */}
        <div
          className={`cinematic-reticle${progress < 0.68 ? ' visible' : ''}`}
          style={{
            '--scroll-p': progress,
          }}
        >
          {/* Horizontal lines */}
          <div className="reticle-line reticle-line--h reticle-line--h1" />
          <div className="reticle-line reticle-line--h reticle-line--h2" />
          {/* Vertical lines */}
          <div className="reticle-line reticle-line--v reticle-line--v1" />
          <div className="reticle-line reticle-line--v reticle-line--v2" />
          {/* 4-Pointed star sparkles at intersections */}
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className={`reticle-sparkle reticle-sparkle--${n}`}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z"
                  fill="rgba(255,255,255,0.75)"
                />
              </svg>
            </div>
          ))}
        </div>

        {/* ─── TEXT LAYER ─────────────────────────── */}
        <div className="cinematic-text-layer">

          {/* ══════════════════════════════════════════
              INITIAL STATE — Visible before scrolling
              Title + Subtitle + CTAs, like pear.no hero
              ══════════════════════════════════════════ */}
          <div
            className="cinematic-beat cinematic-initial"
            style={{
              opacity: initialOpacity,
              transform: `translateY(${initialTranslateY}px)`,
              pointerEvents: initialOpacity > 0.3 ? 'auto' : 'none',
            }}
          >
            {/* Title */}
            <h1
              className={`cinematic-title ${initialLoaded ? 'revealed' : ''}`}
              style={{
                clipPath: initialLoaded ? 'inset(0 0 0 0)' : 'inset(100% 0 0 0)',
                letterSpacing: initialLoaded ? '0.02em' : '0.08em',
                transition: 'clip-path 0.8s cubic-bezier(0.16, 1, 0.3, 1), letter-spacing 1s ease-out',
              }}
            >
              GIMNASIO ENERGY{' '}
              <span className="accent">HUÁNUCO</span>
            </h1>

            {/* Subtitle */}
            <p
              className="cinematic-subtitle"
              style={{
                marginTop: '1.5rem',
                opacity: initialLoaded ? 1 : 0,
                transform: initialLoaded ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.7s ease-out 0.4s, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.4s',
              }}
            >
              El gym en Huánuco con las mejores rutinas
              de ejercicio con resultados reales.
            </p>

            {/* CTA Buttons */}
            <div
              className="cinematic-cta-group"
              style={{
                marginTop: '2rem',
                opacity: initialLoaded ? 1 : 0,
                transform: initialLoaded ? 'translateY(0)' : 'translateY(16px)',
                transition: 'opacity 0.6s ease-out 0.7s, transform 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.7s',
              }}
            >
              <Link
                to="/descargar-app"
                className="cinematic-cta-primary visible"
              >
                <span>Comienza a entrenar</span>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>

              <a
                href="#tarifas"
                className="cinematic-cta-secondary visible"
              >
                Conoce el gimnasio
              </a>
            </div>
          </div>

          {/* ══════════════════════════════════════════
              BEAT: About Title + Body + Badges
              ══════════════════════════════════════════ */}
          {/* ══════════════════════════════════════════
              BEAT: About Title + Body + Badges
              ══════════════════════════════════════════ */}
          <div
            className="cinematic-beat"
            style={{
              opacity: aboutTitleOpacity,
              pointerEvents: aboutTitleOpacity > 0.3 ? 'auto' : 'none',
            }}
          >
            <h2
              className={`cinematic-about-title ${aboutTitleReveal > 0.15 ? 'revealed' : ''}`}
              style={{
                clipPath: aboutTitleReveal > 0.15
                  ? 'none'
                  : `inset(-25% ${(1 - aboutTitleReveal) * 100}% -10% 0)`,
              }}
            >
              15 AÑOS GUIANDO TU<br />
              <span className="accent">TRANSFORMACIÓN FÍSICA</span>
            </h2>

            <p
              className={`cinematic-about-body ${aboutBodyReveal > 0.2 ? 'visible' : ''}`}
              style={{ opacity: aboutBodyOpacity > 0 ? undefined : 0 }}
            >
              Con 15 años de experiencia, somos el gym en Huánuco de confianza
              cambiando vidas y alcanzando metas físicas reales. Te ayudamos
              a construir hábitos saludables con ejercicio, ya sea que busques
              bajar de peso en el gimnasio, ganar músculo o mejorar tu
              rendimiento deportivo con rutinas estructuradas y constancia.
            </p>

            <div
              className="cinematic-badges"
              style={{ opacity: aboutBadgesOpacity }}
            >
              <span
                className={`cinematic-badge accent-badge ${aboutBadgesReveal > 0.15 ? 'visible' : ''}`}
                style={{ transitionDelay: '0s' }}
              >
                Disciplina y Fuerza
              </span>
              <span
                className={`cinematic-badge ${aboutBadgesReveal > 0.35 ? 'visible' : ''}`}
                style={{ transitionDelay: '0.08s' }}
              >
                Asesoría Continua
              </span>
              <span
                className={`cinematic-badge ${aboutBadgesReveal > 0.55 ? 'visible' : ''}`}
                style={{ transitionDelay: '0.16s' }}
              >
                Resultados Reales
              </span>
            </div>
          </div>

          {/* ══════════════════════════════════════════
              BEAT: Final CTA
              ══════════════════════════════════════════ */}
          <div
            className="cinematic-beat"
            style={{
              opacity: ctaOpacity,
              alignItems: 'flex-start',
              justifyContent: 'center',
              pointerEvents: ctaOpacity > 0.3 ? 'auto' : 'none',
            }}
          >
            <h2
              className={`cinematic-about-title ${ctaReveal > 0.15 ? 'revealed' : ''}`}
              style={{
                clipPath: ctaReveal > 0.15 ? 'inset(0 0 0 0)' : `inset(0 ${(1 - ctaReveal) * 100}% 0 0)`,
                fontSize: 'clamp(2.4rem, 9.5vw, 4.5rem)',
                lineHeight: 0.95,
                marginBottom: '1rem',
              }}
            >
              EMPIEZA TU{' '}
              <span className="accent">CAMBIO HOY</span>
            </h2>

            <div className="cinematic-cta-group">
              <Link
                to="/descargar-app"
                className={`cinematic-cta-primary ${ctaReveal > 0.25 ? 'visible' : ''}`}
                style={{ transitionDelay: '0.1s' }}
              >
                <span>Comienza a entrenar</span>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>

              <a
                href="#tarifas"
                className={`cinematic-cta-secondary ${ctaReveal > 0.45 ? 'visible' : ''}`}
                style={{ transitionDelay: '0.2s' }}
              >
                Conoce el gimnasio
              </a>
            </div>
          </div>
        </div>

        {/* ─── PROGRESS INDICATOR ─────────────────── */}
        <div className="cinematic-progress">
          <div
            className="cinematic-progress-fill"
            style={{ height: `${progress * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default ScrollCinematicExperience;
